import {useAuth} from '@/util/auth';
import {useStableCallback} from '@/util/hooks/useStableCallback';
import {useDispatch} from 'react-redux';
import {
    addToolToBookmarks,
    addCaseToBookmarks,
    addToolToFavorites,
    addCaseToFavorites,
    deleteToolFromBookmarks,
    deleteCaseFromBookmarks,
    deleteCaseFromFavorites,
    deleteToolFromFavorites
} from '@/features/thunks';
import React, {useEffect} from 'react';
import {fetchCurrentUserBookmarks, postDeleteBookmark} from '@/util/users';

export const useAddCaseBookmarks = (
    caseId: string
) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    return useStableCallback(() => {
        if (!auth.isAuthenticated) {
            return;
        }
        dispatch(addCaseToBookmarks({caseId}));
    });
}

export const useDeleteCaseBookmarks = ({
    caseId,
    bookmarkId
}: {
    caseId: string,
    bookmarkId: string
}) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    return useStableCallback(() => {
        if (!auth.isAuthenticated) {
            return;
        }
        dispatch(deleteCaseFromBookmarks({caseId, bookmarkId}));
    });
}

export const useDeleteToolBookmarks = ({
    toolId,
    bookmarkId
}: {
    toolId: string,
    bookmarkId: string
}) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    return useStableCallback(() => {
        if (!auth.isAuthenticated) {
            return;
        }
        dispatch(deleteToolFromBookmarks({
            toolId,
            bookmarkId
        }));
    });
}

export const useDeleteToolFavorites = ({
    toolId,
    bookmarkId
}: {
    toolId: string,
    bookmarkId: string
}) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    return useStableCallback(() => {
        if (!auth.isAuthenticated) {
            return;
        }
        dispatch(deleteToolFromFavorites({
            toolId,
            bookmarkId
        }));
    });
}

export const useDeleteCaseFavorites = ({
    caseId,
    bookmarkId
}: {
    caseId: string,
    bookmarkId: string
}) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    return useStableCallback(() => {
        if (!auth.isAuthenticated) {
            return;
        }
        dispatch(deleteCaseFromFavorites({
            caseId,
            bookmarkId
        }));
    });
}

export const useAddCaseToFavorites = (
    caseId: string
) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    return useStableCallback(() => {
        if (!auth.isAuthenticated) {
            return;
        }
        dispatch(addCaseToFavorites({caseId}));
    });
}

export const useAddToolToFavorites = (
    toolId: string
) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    return useStableCallback(() => {
        if (!auth.isAuthenticated) {
            return;
        }
        dispatch(addToolToFavorites({toolId}));
    });
}

export const useAddToolBookmarks = (
    toolId: string
) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    return useStableCallback(() => {
        if (!auth.isAuthenticated) {
            return;
        }
        dispatch(addToolToBookmarks({toolId}));
    });
}

export const useBookmarks = () => {
    const [bookmarks, setBookmarks] = React.useState<Bookmark[]>([]);

    useEffect(() => {
        fetchCurrentUserBookmarks().then((bookmarks) => {
            setBookmarks(bookmarks as Bookmark[])
        });
    }, []);

    const deleteFromBookmarks = useStableCallback((bookmarkId) => {
        postDeleteBookmark(bookmarkId).then(() => {
            fetchCurrentUserBookmarks().then((bookmarks) => {
                setBookmarks(bookmarks as Bookmark[])
            });
        })
    });

    return [bookmarks, deleteFromBookmarks] as const;
}
